import Plotly from 'plotly.js-dist';

console.log('📦 frameworkvisualizer.js module loaded');

// Framework configuration - EXACTLY matches Python code
const splits = [4, 4, 4, 3, 8, 8, 6, 6, 11, 11, 7, 4];  // 12 sections, 76 total sub-elements

const section_names = [
  'Outcomes',
  'Objectives', 
  'Outputs',
  'Functions',
  'Threats',
  'Opportunities',
  'Strategy',
  'Governance and Organization',
  'Financing',
  'Resource Management',
  'Health Services',
  'Implementation'
];

// Framework definitions
const FRAMEWORK_DEFINITIONS = {
  'Outcomes': 'How well is the health system performing in relation to cancer care and control concerning health system outcomes?',
  'Objectives': 'How well is the health system producing health services for cancer in terms of the four value dimensions?',
  'Outputs': 'Is the health system delivering the right health services to respond to cancer?',
  'Functions': 'How are health system inputs such as governance and organisation, financing, and resource management currently generated and used in health systems to deliver public and individual health services for cancer control?',
  'Threats': 'What contextual threats will influence cancer demands on the health system and influence the system response to cancer care and control?',
  'Opportunities': 'What contextual opportunities will influence cancer demands on the health system and influence the system response to cancer care and control?',
  'Strategy': 'How will the challenge identified be addressed to improve health system performance and create value in relation to cancer?',
  'Governance and Organization': 'What reforms are to be undertaken in terms of governance and organization of the health system to achieve the articulated strategy to improve cancer care and control?',
  'Financing': 'What reforms are to be undertaken in terms of financing of the health system to achieve the articulated strategy to improve cancer care and control?',
  'Resource Management': 'What reforms are to be undertaken in terms of resource management of the health system to achieve the articulated strategy to improve cancer care and control?',
  'Health Services': 'What reforms are to be undertaken in terms of the delivery of health services to achieve the articulated strategy to improve cancer care and control?',
  'Implementation': 'How will the plan be implemented, measured, stakeholder alignment and sustained changed management achieved?'
};

const max_scores_dict = {
  // Outcomes (4)
  "Health": 5, 
  "Financial Risk Protection": 5, 
  "User Satisfaction": 5,
  "Psychosocial Support Services": 5,
  
  // Objectives (4)
  "Effectiveness": 5,
  "Efficiency": 4, 
  "Equity": 4, 
  "Responsiveness": 4,
  
  // Outputs (4)
  "Individual Health Services": 5,
  "Population Health Services": 4,
  "Community-Based Palliative Care": 5,
  "Cancer Surveillance Systems": 5,
  
  // Functions (3)
  "Governance and Organisation": 5, 
  "Financing": 5, 
  "Resource Management": 5,
  
  // Threats (8)
  "Demographic Threats": 4, 
  "Epidemiologic Threats": 4, 
  "Political Threats": 4, 
  "Legal Threats": 4,
  "Sociocultural Threats": 4, 
  "Economic Threats": 4, 
  "Ecological Threats": 4, 
  "Technological Threats": 4,
  
  // Opportunities (8)
  "Demographic Opportunities": 4, 
  "Epidemiologic Opportunities": 4, 
  "Political Opportunities": 4,
  "Legal Opportunities": 4, 
  "Sociocultural Opportunities": 4, 
  "Economic Opportunities": 4,
  "Ecological Opportunities": 4, 
  "Technological Opportunities": 4,
  
  // Strategy (6)
  "Vision": 3,
  "Mission": 3,
  "Goals": 3, 
  "Objectives": 3, 
  "Values": 2,
  "Integration with Noncommunicable Disease Programs": 5,
  
  // Governance and Organization (6)
  "Macro-organization": 2, 
  "Governance": 5, 
  "Policy": 5, 
  "Regulation": 5,
  "Decentralization": 2, 
  "Strategic public private partnerships": 5,
  
  // Financing (11)
  "Cost measurement systems": 3, 
  "Current financing and fiscal space": 5,
  "Proposed funding to implement plan": 3, 
  "Sources of funds": 5, 
  "Pooling of funds": 2, 
  "Channeling of funds": 2, 
  "Allocation of funds": 4,
  "Payment mechanisms for providers": 4, 
  "Payment mechanisms for capital investments": 3,
  "Resource Mobilization Strategies": 5,
  "Financing for Governance and Oversight": 5,
  
  // Resource Management (11)
  "Human resources": 5, 
  "Capacity Building for Health Professionals": 5,
  "Infrastructure": 5,
  "Pharmaceuticals and medical supplies": 5,
  "Patient Navigation": 5,
  "Information technology and data systems": 5, 
  "Supply chain management": 3,
  "Knowledge Generation": 3,
  "Innovation ecosystem": 3, 
  "Education Initiatives": 5,
  "Innovation Initiatives": 5,
  
  // Health Services (7)
  "Public Health Services (Health promotion)": 5, 
  "Public Health Services (Health protection)": 5,
  "Public Health Services (Disease Prevention)": 5, 
  "Personal Healthcare Services (Diagnosis)": 4,
  "Personal Healthcare Services (Treatment)": 4,
  "Personal Healthcare Services (Palliation and rehabilitative care)": 4,
  "Provider Value Enhancement": 3,
  
  // Implementation (4)
  "Monitoring and Evaluation framework": 2,
  "Change management": 2,
  "Risk and Mitigation Strategies": 3,
  "Stakeholder engagement": 3
};

// Build reverse mapping: sub-element -> section
const SUBELEMENT_TO_SECTION = {};
const subElementKeys = Object.keys(max_scores_dict);
let cumulativeIndex = 0;

splits.forEach((splitCount, sectionIndex) => {
  const sectionName = section_names[sectionIndex];
  for (let i = 0; i < splitCount; i++) {
    const subElement = subElementKeys[cumulativeIndex + i];
    if (subElement) {
      SUBELEMENT_TO_SECTION[subElement] = sectionName;
    }
  }
  cumulativeIndex += splitCount;
});

console.log('✅ Framework configuration loaded:', {
  sections: section_names.length,
  subElements: Object.keys(max_scores_dict).length,
  splits
});

// Load data from CSV
const loadAllCountriesData = async () => {
  console.log('🔄 Starting to load CSV data...');
  try {
    const response = await fetch('/data/s3_cancer_plan_output.csv');
    console.log('✅ Fetch response status:', response.status, response.statusText);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const text = await response.text();
    console.log('✅ CSV loaded, length:', text.length, 'characters');
    console.log('📄 First 200 chars:', text.substring(0, 200));
    
    // Parse CSV
    const lines = text.split('\n');
    console.log('📊 Total lines in CSV:', lines.length);
    
    const headers = lines[0].split(',');
    console.log('📋 Headers:', headers);
    
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      
      const values = [];
      let currentValue = '';
      let insideQuotes = false;
      
      for (let char of lines[i]) {
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim());
      
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => {
          row[header.trim()] = values[index];
        });
        data.push(row);
      }
    }
    
    console.log('✅ Parsed', data.length, 'data rows');
    console.log('🌍 First row sample:', data[0]);
    
    // Get unique countries
    const uniqueCountries = [...new Set(data.map(row => row.country))];
    console.log('🌎 Unique countries found:', uniqueCountries.length, uniqueCountries);
    
    return data;
  } catch (error) {
    console.error('❌ Error loading data:', error);
    console.error('❌ Error details:', error.message, error.stack);
    throw error;
  }
};

// Get list of available countries
const getAvailableCountries = (data) => {
  const countries = [...new Set(data.map(row => row.country))];
  return countries.sort();
};

// Filter data by country (no year needed)
const filterDataByCountry = (data, country) => {
  return data.filter(row => row.country === country);
};

// Calculate overall country score for map visualization
const calculateCountryScore = (countryData) => {
  if (!countryData || countryData.length === 0) return 0;
  
  const scores = countryData
    .map(row => parseFloat(row.score))
    .filter(score => !isNaN(score));
  
  if (scores.length === 0) return 0;
  
  const sum = scores.reduce((a, b) => a + b, 0);
  return (sum / scores.length).toFixed(2);
};

// Get country scores for all countries (for map coloring)
const getAllCountryScores = (data) => {
  const countries = getAvailableCountries(data);
  const countryScores = {};
  
  countries.forEach(country => {
    const countryData = filterDataByCountry(data, country);
    countryScores[country] = calculateCountryScore(countryData);
  });
  
  return countryScores;
};

// Parse framework data
const parseFrameworkData = (countryData) => {
  const scores = [];
  const sub_elements = [];
  
  countryData.forEach(row => {
    const subElement = row['Sub-Element'];
    const score = parseFloat(row.score);
    
    if (!isNaN(score) && SUBELEMENT_TO_SECTION[subElement]) {
      scores.push(score);
      sub_elements.push(subElement);
    }
  });
  
  return { scores, sub_elements };
};

// Calculate averaged and normalized scores by section - EXACTLY matches Python logic
const getAveragedNormalizedData = (scores, subElements) => {
  console.log('📊 Calculating averaged normalized data...');
  console.log('Scores:', scores);
  console.log('Sub-elements:', subElements);
  
  const sections = [];
  let index = 0;
  
  splits.forEach((count, sectionIndex) => {
    const sectionScores = [];
    const sectionSubElements = subElements.slice(index, index + count);
    const rawScores = scores.slice(index, index + count);
    
    // Normalize each score by its max, then scale to 5
    rawScores.forEach((score, i) => {
      const subElement = sectionSubElements[i];
      const maxScore = max_scores_dict[subElement] || 5;
      const normalized = (score / maxScore) * 5;
      sectionScores.push(normalized);
    });
    
    // Average the normalized scores for this section
    const avg = sectionScores.length > 0 
      ? sectionScores.reduce((a, b) => a + b, 0) / sectionScores.length 
      : 0;
    
    console.log(`Section ${section_names[sectionIndex]}: raw=[${rawScores.join(',')}], normalized=[${sectionScores.map(s => s.toFixed(2)).join(',')}], avg=${avg.toFixed(2)}`);
    
    sections.push(avg);
    index += count;
  });
  
  console.log('✅ Final averaged values:', sections);
  return sections;
};

// Get section data for distribution chart
const getSectionData = (sectionName, parsedData) => {
  const { scores, sub_elements } = parsedData;
  const sectionScores = [];
  const sectionSubElements = [];
  
  sub_elements.forEach((element, index) => {
    if (SUBELEMENT_TO_SECTION[element] === sectionName) {
      sectionScores.push(scores[index]);
      sectionSubElements.push(element);
    }
  });
  
  return {
    scores: sectionScores,
    sub_elements: sectionSubElements
  };
};

// UPDATED: Get detailed section data with text/reasoning from original CSV data
const getSectionDetailedData = (sectionName, countryData) => {
  console.log('📝 Getting detailed data for section:', sectionName);
  
  const sectionData = countryData.filter(row => {
    const subElement = row['Sub-Element'];
    return SUBELEMENT_TO_SECTION[subElement] === sectionName;
  });
  
  console.log('✅ Found', sectionData.length, 'rows for section');
  
  return sectionData.map(row => ({
    subElement: row['Sub-Element'],
    score: parseFloat(row.score),
    maxScore: max_scores_dict[row['Sub-Element']] || 5,
    normalizedScore: (parseFloat(row.score) / (max_scores_dict[row['Sub-Element']] || 5)) * 5,
    response: row.response || 'No response available',
    reasoning: row.reasoning || 'No reasoning available'
  }));
};

// Render interactive world map
const renderWorldMap = (container, allCountriesData, onCountryClick) => {
  console.log('🗺️ renderWorldMap called with:', {
    containerExists: !!container,
    dataLength: allCountriesData?.length,
    hasClickHandler: !!onCountryClick
  });
  
  const countryScores = getAllCountryScores(allCountriesData);
  
  // Prepare data for Plotly choropleth
  const countries = Object.keys(countryScores);
  const scores = Object.values(countryScores).map(Number);
  
  console.log('🌍 Countries with data:', countries);
  console.log('📊 Country scores:', countryScores);
  console.log('🔢 Number of countries:', countries.length);
  
  // Convert country names to ISO-3 codes for Plotly
  const countryToISO3 = {
    'Argentina': 'ARG', 'Australia': 'AUS', 'Austria': 'AUT', 'Bangladesh': 'BGD',
    'Belgium': 'BEL', 'Brazil': 'BRA', 'Canada': 'CAN', 'Chile': 'CHL',
    'China': 'CHN', 'Colombia': 'COL', 'Costa Rica': 'CRI', 'Croatia': 'HRV',
    'Cyprus': 'CYP', 'Czech Republic': 'CZE', 'Denmark': 'DNK', 'Ecuador': 'ECU',
    'Egypt': 'EGY', 'Estonia': 'EST', 'Finland': 'FIN', 'France': 'FRA',
    'Germany': 'DEU', 'Ghana': 'GHA', 'Greece': 'GRC', 'Hungary': 'HUN',
    'Iceland': 'ISL', 'India': 'IND', 'Indonesia': 'IDN', 'Iran': 'IRN',
    'Ireland': 'IRL', 'Israel': 'ISR', 'Italy': 'ITA', 'Jamaica': 'JAM',
    'Japan': 'JPN', 'Jordan': 'JOR', 'Kenya': 'KEN', 'Latvia': 'LVA',
    'Lebanon': 'LBN', 'Lithuania': 'LTU', 'Luxembourg': 'LUX', 'Malaysia': 'MYS',
    'Mexico': 'MEX', 'Morocco': 'MAR', 'Netherlands': 'NLD', 'New Zealand': 'NZL',
    'Nigeria': 'NGA', 'Norway': 'NOR', 'Pakistan': 'PAK', 'Panama': 'PAN',
    'Peru': 'PER', 'Philippines': 'PHL', 'Poland': 'POL', 'Portugal': 'PRT',
    'Romania': 'ROU', 'Russia': 'RUS', 'Saudi Arabia': 'SAU', 'Serbia': 'SRB',
    'Singapore': 'SGP', 'Slovakia': 'SVK', 'Slovenia': 'SVN', 'South Africa': 'ZAF',
    'South Korea': 'KOR', 'Spain': 'ESP', 'Sri Lanka': 'LKA', 'Sweden': 'SWE',
    'Switzerland': 'CHE', 'Thailand': 'THA', 'Tunisia': 'TUN', 'Turkey': 'TUR',
    'Uganda': 'UGA', 'Ukraine': 'UKR', 'United Arab Emirates': 'ARE', 'United Kingdom': 'GBR',
    'United States': 'USA', 'Uruguay': 'URY', 'Vietnam': 'VNM', 'Zimbabwe': 'ZWE'
  };
  
  const locationCodes = countries.map(country => countryToISO3[country] || country);
  
  // Binary color scheme: just show which countries have data
  const binaryValues = countries.map(() => 1); // All countries with data get value 1
  
  const data = [{
    type: 'choropleth',
    locations: locationCodes,
    z: binaryValues,
    text: countries,
    customdata: scores,
    hovertemplate: '<b>%{text}</b><br>Average Score: %{customdata:.2f}<br><i>Click to view details</i><extra></extra>',
    colorscale: [
      [0, '#E5E7EB'],  // Gray for no data (won't be used since we only show countries with data)
      [1, '#113743']   // Blue for countries with data
    ],
    showscale: false,  // Hide the color scale legend
    marker: {
      line: {
        color: 'white',
        width: 1.5
      }
    }
  }];
  
  const layout = {
    title: {
      text: 'National Cancer Control Plan Framework Scores by Country',
      font: { size: 20, color: '#1F2937' }
    },
    geo: {
      projection: {
        type: 'natural earth'
      },
      showframe: false,
      showcoastlines: true,
      coastlinecolor: '#D1D5DB',
      showcountries: true,
      countrycolor: '#E5E7EB',
      bgcolor: '#F9FAFB'
    },
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    margin: { t: 60, b: 20, l: 0, r: 0 },
    height: 500
  };
  
  const config = {
    responsive: true,
    displayModeBar: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d']
  };
  
  Plotly.newPlot(container, data, layout, config);
  
  // Add click event listener
  container.on('plotly_click', function(data) {
    if (data.points && data.points.length > 0) {
      const clickedCountry = data.points[0].text;
      if (onCountryClick && clickedCountry) {
        onCountryClick(clickedCountry);
      }
    }
  });
};

// COMPLETELY REWRITTEN: Render radial BAR chart
const renderRadialChart = (container, averagedScores, sectionNames) => {
  console.log('📊 renderRadialChart called with:', { scoresLength: averagedScores.length, sectionsLength: sectionNames.length });
  
  // Color palette for sections
  const colors = [
    '#06B6D4', // Cyan - Outcomes
    '#FBBF24', // Yellow - Objectives
    '#A78BFA', // Purple - Outputs  
    '#113743', // Blue - Functions
    '#EC4899', // Pink - Threats
    '#9CA3AF', // Gray - Opportunities
    '#FB923C', // Orange - Strategy
    '#60A5FA', // Light Blue - Governance
    '#38BDF8', // Sky Blue - Financing
    '#22D3EE', // Teal - Resource Management
    '#F97316', // Dark Orange - Health Services
    '#2DD4BF'  // Turquoise - Implementation
  ];
  
  // CRITICAL FIX: Create a SINGLE trace with ALL bars, not separate traces
  const data = [{
    type: 'barpolar',
    r: averagedScores,
    theta: sectionNames,
    marker: {
      color: colors,
      line: {
        color: 'white',
        width: 2
      }
    },
    hovertemplate: '<b>%{theta}</b><br>Score: %{r:.2f} / 5.0<br><i>Click to view details</i><extra></extra>'
  }];
  
  // Get container dimensions for responsive sizing
  const containerHeight = container.offsetHeight || 600;
  
  const layout = {
    polar: {
      radialaxis: {
        visible: true,
        range: [0, 5],
        tickvals: [0, 1, 2, 3, 4, 5],
        ticktext: ['0', '1', '2', '3', '4', '5'],
        tickfont: { size: 12, color: '#6B7280' },
        gridcolor: '#E5E7EB',
        showline: false
      },
      angularaxis: {
        tickfont: { size: 10, weight: 'bold', color: '#1F2937' },
        direction: 'clockwise',
        rotation: 90,
        gridcolor: '#E5E7EB'
      },
      bgcolor: 'white',
      bargap: 0.1  // Add gap between bars
    },
    showlegend: false,
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    margin: { 
      t: 30, 
      b: 30, 
      l: containerHeight < 550 ? 60 : 80,  // Smaller margins for smaller charts
      r: containerHeight < 550 ? 60 : 80 
    },
    height: containerHeight,
    autosize: true
  };
  
  const config = {
    responsive: true,
    displayModeBar: false
  };
  
  Plotly.newPlot(container, data, layout, config);
  
  // Add click event for sections
  container.on('plotly_click', function(data) {
    if (data.points && data.points.length > 0) {
      const section = data.points[0].theta;
      console.log('🎯 Section clicked:', section);
      const event = new CustomEvent('sectionClick', { detail: { section } });
      document.dispatchEvent(event);
    }
  });
};

// Render distribution chart for section breakdown
const renderDistributionChart = (container, sectionName, scores, subElements) => {
  // Normalize scores using their individual max scores
  const normalizedScores = scores.map((score, i) => {
    const subElement = subElements[i];
    const maxScore = max_scores_dict[subElement] || 5;
    return (score / maxScore) * 5;
  });
  
  const data = [{
    type: 'bar',
    x: subElements,
    y: normalizedScores,
    marker: {
      color: normalizedScores,
      colorscale: [
        [0, '#fee5d9'],
        [0.33, '#fcae91'],
        [0.67, '#fb6a4a'],
        [1, '#cb181d']
      ],
      line: {
        color: '#374151',
        width: 1
      }
    },
    hovertemplate: '<b>%{x}</b><br>Normalized Score: %{y:.1f} / 5.0<extra></extra>'
  }];
  
  const layout = {
    title: {
      text: `${sectionName} - Sub-Element Scores`,
      font: { size: 16, color: '#1F2937' }
    },
    xaxis: {
      title: '',
      tickangle: -45,
      tickfont: { size: 11 }
    },
    yaxis: {
      title: 'Normalized Score',
      range: [0, 5],
      tickvals: [0, 1, 2, 3, 4, 5],
      tickfont: { size: 12 }
    },
    paper_bgcolor: 'white',
    plot_bgcolor: '#f9fafb',
    margin: { t: 60, b: 120, l: 60, r: 40 }
  };
  
  const config = {
    responsive: true,
    displayModeBar: false
  };
  
  Plotly.newPlot(container, data, layout, config);
};

// Export all functions as default object
export default {
  splits,
  section_names,
  FRAMEWORK_DEFINITIONS,
  SUBELEMENT_TO_SECTION,
  max_scores_dict,
  loadAllCountriesData,
  getAvailableCountries,
  filterDataByCountry,
  calculateCountryScore,
  getAllCountryScores,
  parseFrameworkData,
  getAveragedNormalizedData,
  getSectionData,
  getSectionDetailedData,
  renderWorldMap,
  renderRadialChart,
  renderDistributionChart
};